import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../utils/AuthContext';

const ExpenseModal = ({ isOpen, onClose, onSave, expenseToEdit }) => {
    const { user } = useAuth();
    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const categories = [
        'Staff Payment',
        'Transport Expense',
        'Office',
        'Maintenance',
        'Miscellaneous'
    ];

    useEffect(() => {
        if (isOpen) {
            if (expenseToEdit) {
                reset({
                    expenseDate: expenseToEdit.expense_date,
                    category: expenseToEdit.category,
                    amount: expenseToEdit.amount,
                    paymentMode: expenseToEdit.payment_mode,
                    notes: expenseToEdit.notes
                });
            } else {
                reset({
                    expenseDate: new Date().toISOString().split('T')[0],
                    category: 'Staff Payment',
                    amount: '',
                    paymentMode: 'Cash',
                    notes: ''
                });
            }
        }
    }, [isOpen, expenseToEdit, reset]);

    const onSubmit = async (data) => {
        if (!user) {
            toast.error("User not authenticated");
            return;
        }

        try {
            const expenseData = {
                user_id: user.id,
                expense_date: data.expenseDate,
                category: data.category,
                amount: parseFloat(data.amount),
                payment_mode: data.paymentMode,
                notes: data.notes
            };

            let error;
            if (expenseToEdit) {
                const { error: updateError } = await supabase
                    .from('expenses')
                    .update(expenseData)
                    .eq('id', expenseToEdit.id);
                error = updateError;
            } else {
                const { error: insertError } = await supabase
                    .from('expenses')
                    .insert([expenseData]);
                error = insertError;
            }

            if (error) throw error;

            toast.success(expenseToEdit ? 'Expense updated successfully' : 'Expense added successfully');
            onSave(); // Refresh parent list
            onClose();
        } catch (error) {
            console.error('Error saving expense:', error);
            toast.error('Failed to save expense');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">
                        {expenseToEdit ? 'Edit Expense' : 'Add New Expense'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">

                    {/* Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                        <input
                            type="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            {...register('expenseDate', { required: 'Date is required' })}
                        />
                        {errors.expenseDate && <p className="text-red-500 text-xs mt-1">{errors.expenseDate.message}</p>}
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            {...register('category', { required: 'Category is required' })}
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>

                    {/* Amount */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
                        <input
                            type="number"
                            step="0.01"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0.00"
                            {...register('amount', {
                                required: 'Amount is required',
                                min: { value: 0.01, message: 'Amount must be greater than 0' }
                            })}
                        />
                        {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount.message}</p>}
                    </div>

                    {/* Payment Mode */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            {...register('paymentMode')}
                        >
                            <option value="Cash">Cash</option>
                            <option value="UPI">UPI</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Cheque">Cheque</option>
                        </select>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Description</label>
                        <textarea
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Optional details..."
                            {...register('notes')}
                        ></textarea>
                    </div>

                    {/* Buttons */}
                    <div className="pt-4 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            Save Expense
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExpenseModal;
